import React, { useState, useContext, useEffect } from "react";
import {
  Grid,
  Typography,
  FormControlLabel,
  Avatar,
  Button,
  Box,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Checkbox, Container, Paper } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Form, useForm } from "../../../components/mui/useForm";
import useSessionStorage from "../../../components/useSessionStorage";
import useLocalStorage from "../../../components/useLocalStorage";
import { MuiButton, MuiInput } from "../../../components";
import LoadingButton from "@mui/lab/LoadingButton";
import * as authService from "../services/AuthService";
import * as userService from "../services/UserService";
import Notification from "../../../components/mui/Notification";
import { useNavigate, useLocation } from "react-router-dom";
import { loginSuccess } from "../reducers/actions";
import { AppContext } from "../App";
import { FORGOT_PASS_ROUTE, SINGUP_ROUTE } from "../helper/Constants";
import { useTranslation } from "react-i18next";
import * as actuatorService from "../services/ActuatorService";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: 20,
    margin: "20px auto",
  },
  btn: {
    margin: "8px 0",
  },
}));

const initialFValues = {
  username: "",
  password: "",
};

export default function Login(props) {
  const { t } = useTranslation();
  const { appState, dispatch } = useContext(AppContext);
  const classes = useStyles();
  const navigate = useNavigate();
  const setAuthToken = useSessionStorage("authToken")[1];
  const { state } = useLocation();
  const [rememberMe, setRememberMe] = useLocalStorage("rememberMe", true);
  const [localUsername, setLocalUsername] = useLocalStorage("userName");
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ("username" in fieldValues)
      temp.username =
        fieldValues.username !== "" && /$^|.+@.+..+/.test(fieldValues.username)
          ? ""
          : "Email is not valid.";
    if ("password" in fieldValues)
      temp.password = fieldValues.password ? "" : "This field is required.";
    setErrors({
      ...temp,
    });

    if (fieldValues === values)
      return Object.values(temp).every((x) => x === "");
  };

  const handleNavigation = () => {
    if (state?.redirect) {
      navigate(state.redirect);
    } else if (appState.lastVisitedPage && appState.lastVisitedPage !== "") {
      navigate(appState.lastVisitedPage);
    } else {
      navigate("/");
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    true,
    validate
  );

  useEffect(() => {
    actuatorService.health();
    if (localUsername) {
      setValues({ ...values, username: localUsername });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      authService
        .login(values)
        .then((response) => {
          handleRememberMeInLocalStorage();
          setAuthToken(response.data.token);
          userService
            .profile()
            .then((res) => {
              dispatch(loginSuccess(res.data));
              handleNavigation();
            })
            .catch((e) => {
              dispatch(loginSuccess({ firstName: "Guest" }));
              handleError(e);
              handleNavigation();
            });
          // dispatch(loginSuccess(response.data));
          // handleNavigation();
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => setLoading(false));
    }
  };

  function handleError(error) {
    if (error.response) {
      console.log("error.response: ", error.response);

      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === 428) {
        setNotify({
          isOpen: true,
          message: "You should verify your email address",
          type: "error",
        });
      } else if (error.response.status === 417) {
        setNotify({
          isOpen: true,
          message: "You do not have access",
          type: "error",
        });
      } else {
        setNotify({
          isOpen: true,
          message: "Username or password is not correct",
          type: "error",
        });
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.log("error.request: ", error.request);
      setNotify({
        isOpen: true,
        message: "Could not connect to server. Please try again later",
        type: "error",
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log("error.config: ", error.config);
    }
  }

  function handleRememberMeInLocalStorage() {
    if (rememberMe) {
      setLocalUsername(values.username);
    } else setLocalUsername(undefined);
  }

  const handleRememberMe = (event) => {
    setRememberMe(event.target.checked);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Form onSubmit={handleSubmit}>
        <Paper elevation={10} className={classes.paper}>
          <Grid container justifyContent={"center"}>
            <Grid item xs={12} align="center">
              <Avatar sx={{ bgcolor: "#1bbd7e" }}>
                <LockOutlinedIcon />
              </Avatar>
              <h2>{t("login_title")}</h2>
              <MuiInput
                name="username"
                label={t("form_email_address")}
                value={values.username}
                onChange={handleInputChange}
                error={errors.username}
              />
              <MuiInput
                name="password"
                label={t("form_password")}
                type="password"
                value={values.password}
                onChange={handleInputChange}
                error={errors.password}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                sx={{ ml: 2 }}
                control={
                  <Checkbox
                    name="rememberMe"
                    onChange={handleRememberMe}
                    color="primary"
                    checked={rememberMe}
                  />
                }
                label={t("remember_me")}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  mb: 2,
                  ml: 3,
                }}
              >
              <LoadingButton
                type="submit"
                sx={{ mt: 1, mr: 1 }}
                color="success"
                variant="contained"
                loading={loading}
              >
                  {t("login_btn")}
              </LoadingButton>
              <MuiButton
                sx={{ mt: 1, mr: 1 }}
                size="medium"
                text={t("form_cancel")}
                color="info"
                variant="contained"
                onClick={() => navigate("/")}
              />
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2, // Adjust the gap between buttons as needed
                  mt: 1,
                  ml: 2,
                }}
            >
              <Button
                variant="text"
                onClick={() => navigate(FORGOT_PASS_ROUTE)}
              >
                {t("forgot_password")}
              </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center", // Align typography and button vertically
                  ml: 3,
                }}
              >
                <Typography variant="body2">{t("login_create_account_prefix")}</Typography>
                <Button variant="text" onClick={() => navigate(SINGUP_ROUTE)}>
                  {t("login_create_account")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Form>
      <Notification notify={notify} setNotify={setNotify} />
    </Container>
  );
}
